<script setup lang="ts">
import { computed } from "vue";

import { backendOld } from "~/api";
import SettingsCopyText from "~/components/settings/SettingsCopyText.vue";
import { useEnvironmentStore, useUserStore } from "~/stores";

const ROTATE_CALENDAR_LINK_DIALOG_DESCRIPTION =
  "Rotating the calendar link will invalidate the old link and make a new one. This can't be undone. Are you sure you want to proceed?";
const DELETE_CALENDAR_LINK_DIALOG_DESCRIPTION =
  "Deleting the calendar link will invalidate the old link. This can't be undone. Are you sure you want to proceed?";

const environmentStore = useEnvironmentStore();
const userStore = useUserStore();

const link = computed(() =>
  userStore.calendarDuid ? `${environmentStore.urlBase}/api/v0/calendar/${userStore.calendarDuid}.ics` : null
);

const rotateCalendarLink = async () => {
  const { data } = await backendOld.profile.rotateCalendarLink();
  userStore.calendarDuid = data.calendarDuid;
};

const deleteCalendarLink = async () => {
  userStore.calendarDuid = null;
  backendOld.profile.deleteCalendarLink();
};
</script>

<template>
  <div class="flex flex-col gap-y-16">
    <div class="flex flex-col justify-center space-y-3">
      <div class="flex justify-between">
        <div class="flex gap-x-4">
          <h2 class="flex select-none items-center text-xl text-md">Calendar integration</h2>
        </div>
      </div>
      <div class="select-none text-sm/relaxed text-lt">
        <p>Integrate Dart with any other calendar application to show tasks with dates there.</p>
        <br />
        <p>
          The link allows you to see all tasks that have a date and are assigned to you. Tasks that finished more than
          three months ago or start in more than a year are not included. Depending on which calendar you use, it can
          sometimes take some time for changes in Dart to show up in the other application.
        </p>
        <br />
        <!-- prettier-ignore -->
        <p>
          To view your Dart tasks in another calendar app, copy the calendar link below and add it to your calendar as a subscription.
          For detailed instructions for popular applications, click
          <a href="https://calendar.google.com/calendar/u/1/r/settings/addbyurl" target="_blank" rel="noopener noreferrer">
            <span class="underline text-vlt">here for Google Calendar</span>
          </a>,
          <a href="https://support.apple.com/guide/calendar/subscribe-to-calendars-icl1022" target="_blank" rel="noopener noreferrer">
            <span class="underline text-vlt">here for Apple Calendar</span>
          </a>,
          <a href="https://support.microsoft.com/en-us/office/import-or-subscribe-to-a-calendar-in-outlook-com-or-outlook-on-the-web-cff1429c-5af6-41ec-a5b4-74f2c278e98c" target="_blank" rel="noopener noreferrer">
            <span class="underline text-vlt">here for Microsoft Outlook</span>
          </a>, or search online for more instructions for the calendar app you use.
        </p>
      </div>
    </div>
    <div class="flex select-none flex-col gap-1">
      <div class="text-md">Calendar link</div>
      <SettingsCopyText
        :text="link"
        label="Calendar link"
        :rotate-dialog-description="ROTATE_CALENDAR_LINK_DIALOG_DESCRIPTION"
        :delete-dialog-description="DELETE_CALENDAR_LINK_DIALOG_DESCRIPTION"
        @rotate-or-create="rotateCalendarLink"
        @delete="deleteCalendarLink" />
    </div>
  </div>
</template>
